import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Btn, Card, Input, Modal, Select, RNLLoader } from '../components/UI';
import { Panel, actionFace, BackBar, ProfileGridStyles, PROFILE_GRID } from '../components/profile-ui';
import Avatar from '../components/Avatar';
import * as api from '../api';
import { userAdminAccess } from '../utils';
import UserPermissionMatrix from '../components/UserPermissionMatrix';
import { MODULE_TAXONOMY } from './UserAccessControl';

/**
 * Create or edit a user, as a full page rather than a modal.
 *
 * Same furniture as the profile page it sits next to — blue panel headers, a
 * 300px rail beside a fluid column — so moving between viewing a user and
 * editing them does not feel like moving between two different applications.
 *
 * `mode` is passed by the route rather than inferred from the presence of a
 * userId, because the two modes differ in more than which fields are filled
 * in: email and password can only be set at creation, and verification is
 * only editable afterwards.
 */
export default function UserFormPage({ mode = 'create' }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', phone: '',
    employee_id: '', job_title: '', grade: '', branch: '',
    is_active: true, is_verified: false, skip_verification: true,
    force_password_change: true,
    mfa_enabled: false,
    role_ids: [], department_ids: [],
  });
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  // the user's own permissions on top of their roles; savedOv = what the server has, to save only on a change
  const [ov, setOv] = useState({ grants: new Set(), revokes: new Set() });
  const [savedOv, setSavedOv] = useState('');

  // Synchronous guard — setSaving is async, so a double click in the same tick
  // would otherwise submit the form twice and create two users.
  const savingRef = useRef(false);
  // The signed-in administrator's Users Management ticks decide which sections they may change here;
  // the server re-checks every change (backend/app/services/user_admin_guard.py).
  const [me, setMe] = useState(null);
  const ua = userAdminAccess(me);
  const isSelfEdit = isEdit && !!me && String(me.user_id) === String(userId);
  const detailsLocked = !!me && (isEdit ? !ua.edit : !ua.create);
  const rolesLocked = !!me && (!ua.assign_roles || isSelfEdit);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleIn = (key, id) => {
    setForm((f) => {
      const list = f[key] || [];
      return { ...f, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });
    // a role change clears the user's revocations (untick again what the new roles should not give)
    if (key === 'role_ids') setOv(o => ({ ...o, revokes: new Set() }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d, who] = await Promise.all([api.listRoles(), api.listDepartments(), api.getMe().catch(() => null)]);
      setMe(who);
      setRoles(Array.isArray(r) ? r : []);
      setDepartments(Array.isArray(d) ? d : (d.departments || d.data || []));

      if (isEdit) {
        // The profile endpoint is the one that returns roles and departments
        // already resolved, so the checkboxes can be ticked without a second
        // pass to match them up.
        const prof = await api.getUserProfile(userId);
        const u = prof.user || {};
        setForm({
          first_name: u.first_name || '', last_name: u.last_name || '',
          email: u.email || '', password: '', phone: u.phone || '',
          employee_id: u.employee_id || '', job_title: u.job_title || '',
          grade: u.grade || '', branch: u.branch || '',
          is_active: !!u.is_active, is_verified: !!u.is_verified,
          skip_verification: true,
          mfa_enabled: !!u.mfa_enabled,
          role_ids: (u.roles || []).map((x) => x.role_id),
          department_ids: (u.departments || []).map((x) => x.id),
        });
        const up = await api.getUserPermissions(userId).catch(() => null);
        if (up) {
          const loaded = { grants: new Set(up.grants || []), revokes: new Set(up.revokes || []) };
          setOv(loaded);
          setSavedOv(JSON.stringify([[...loaded.grants].sort(), [...loaded.revokes].sort()]));
        }
      }
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || 'Could not load this form.');
    }
    setLoading(false);
  }, [isEdit, userId]);

  useEffect(() => { load(); }, [load]);

  const fullName = `${form.first_name} ${form.last_name}`.trim();

  // what the ticked roles give: { permission: [role names] }
  const rolePerms = useMemo(() => {
    const out = {};
    roles.filter(r => form.role_ids.includes(r.role_id)).forEach(r => (r.permissions || []).forEach(p => {
      (out[p] = out[p] || []).push(r.display_name || r.role_name);
    }));
    return out;
  }, [roles, form.role_ids]);
  const isSuperSelected = roles.some(r => form.role_ids.includes(r.role_id) && r.role_name === 'super_admin');
  // what is saved: grants no ticked role gives, revocations of what a ticked role gives
  const ovPayload = () => ({
    grants: [...ov.grants].filter(p => !rolePerms[p]).sort(),
    revokes: [...ov.revokes].filter(p => rolePerms[p]).sort(),
  });
  const saveOverrides = async (id) => {
    const body = ovPayload();
    if (JSON.stringify([body.grants, body.revokes]) === savedOv) return;
    if (!body.grants.length && !body.revokes.length && !savedOv) return;
    await api.saveUserPermissions(id, body);
  };

  const problems = useMemo(() => {
    const out = [];
    if (form.first_name.trim().length < 2) out.push('First name needs at least 2 characters.');
    if (form.last_name.trim().length < 2) out.push('Last name needs at least 2 characters.');
    // Validated in both modes: an administrator can now change the sign-in
    // email of an existing user, so a malformed one must be caught here too.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) out.push('Enter a valid email address.');
    if (!isEdit) {
      // Mirrors the server's UserCreate validator, so the rules are learned
      // here rather than through a rejected submit.
      if (form.password.length < 8) out.push('Password must be at least 8 characters.');
      else if (!/[A-Z]/.test(form.password)) out.push('Password needs an uppercase letter.');
      else if (!/\d/.test(form.password)) out.push('Password needs a number.');
    }
    return out;
  }, [form, isEdit]);

  const canSave = problems.length === 0;

  const save = async () => {
    if (!canSave || savingRef.current) return;
    savingRef.current = true;
    setSaving(true); setSaveError(null);
    try {
      if (isEdit) {
        // Only the fields AdminUserUpdate accepts — sending the whole form
        // would post back email and password, which this endpoint does not
        // own.
        await api.updateUser(userId, {
          // The sign-in identity. The server normalises it, checks it is not
          // already taken, and records the change on the security trail.
          email: form.email.trim().toLowerCase(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone || null,
          is_active: form.is_active,
          is_verified: form.is_verified,
          mfa_enabled: form.mfa_enabled,
          employee_id: form.employee_id || null,
          job_title: form.job_title || null,
          grade: form.grade || null,
          branch: form.branch || null,
          role_ids: form.role_ids,
          department_ids: form.department_ids,
        });
        // after the roles: a role change has just cleared the old revocations on the server too
        if (!rolesLocked) await saveOverrides(userId);
        setOutcome({
          title: 'User Updated',
          message: `${fullName}'s details have been saved.`,
          goTo: `/admin/users/${userId}`,
        });
      } else {
        const res = await api.register({
          email: form.email.trim().toLowerCase(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          password: form.password,
          phone: form.phone || null,
          employee_id: form.employee_id || null,
          job_title: form.job_title || null,
          grade: form.grade || null,
          branch: form.branch || null,
          role_ids: form.role_ids,
          department_ids: form.department_ids,
          skip_verification: form.skip_verification,
          force_password_change: form.force_password_change,
          mfa_enabled: form.mfa_enabled,
        });
        if (res?.user_id && !rolesLocked) {
          try { await saveOverrides(res.user_id); }
          catch (e) { setSaveError(`${fullName} was created, but the permissions were not saved: ${e.message}`); }
        }
        setOutcome({
          title: 'User Created',
          message: res?.message || `${fullName} has been created.`,
          // The id now comes back from the API, so the admin can go straight
          // to the new profile instead of hunting for the row.
          goTo: res?.user_id ? `/admin/users/${res.user_id}` : '/admin?tab=users',
        });
      }
    } catch (e) {
      setSaveError(e.message || 'Could not save this user.');
    }
    savingRef.current = false;
    setSaving(false);
  };

  const backTo = isEdit && userId ? `/admin/users/${userId}` : '/admin?tab=users';

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <RNLLoader title={isEdit ? 'Loading user…' : 'Preparing form…'} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fade-in" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 15, color: 'var(--red)', marginBottom: 16 }}>{loadError}</div>
        <Btn variant="secondary" onClick={() => navigate('/admin?tab=users')}>← Back to Users</Btn>
      </div>
    );
  }

  const checkboxGrid = (items, key, render, locked = () => false) => (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 10, maxHeight: 260, overflowY: 'auto',
    }}>
      {items.map((it) => {
        const id = key === 'role_ids' ? it.role_id : it.id;
        const checked = (form[key] || []).includes(id);
        return (
          <label
            key={id}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 9, cursor: locked(it) ? 'not-allowed' : 'pointer', opacity: locked(it) ? 0.6 : 1,
              padding: '10px 12px', borderRadius: 8, minWidth: 0,
              background: checked
                ? 'color-mix(in srgb, var(--accent) 7%, var(--bg-card))'
                : 'var(--bg-elevated)',
              border: `1px solid ${checked
                ? 'color-mix(in srgb, var(--accent) 32%, transparent)'
                : 'var(--border)'}`,
              transition: 'background 0.12s ease, border-color 0.12s ease',
            }}
          >
            <input
              type="checkbox" checked={checked} disabled={locked(it)}
              onChange={() => toggleIn(key, id)}
              style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
            />
            <span style={{ minWidth: 0 }}>{render(it)}</span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ProfileGridStyles />

      <BackBar
        label={isEdit ? 'Back to Profile' : 'Back to Users'}
        onBack={() => navigate(backTo)}
      >
        {isEdit ? 'Currently editing: ' : 'Currently creating: '}
        <strong style={{ color: 'var(--text-primary)' }}>
          👤 {fullName || (isEdit ? 'User' : 'New User')}
        </strong>
      </BackBar>

      <div className="user-profile-grid" style={PROFILE_GRID}>

        {/* ── Left rail ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, height: '100%' }}>
          <Card style={{ padding: '26px 20px', textAlign: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              {/* Redraws as the names are typed, so the initials the user will
                  be recognised by are visible before saving. */}
              <Avatar first={form.first_name} last={form.last_name} size={104} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {fullName || <span style={{ color: 'var(--text-muted)' }}>Unnamed user</span>}
            </div>
            {form.job_title && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>{form.job_title}</div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
              {form.email || '—'}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 14 }}>
              <Badge color={form.is_active ? 'green' : 'gray'}>{form.is_active ? 'Active' : 'Inactive'}</Badge>
              {isEdit
                ? <Badge color={form.is_verified ? 'green' : 'amber'}>{form.is_verified ? 'Verified' : 'Unverified'}</Badge>
                : <Badge color={form.skip_verification ? 'green' : 'amber'}>
                    {form.skip_verification ? 'Will be verified' : 'Needs verification'}
                  </Badge>}
              <Badge color="purple">{form.role_ids.length} role{form.role_ids.length === 1 ? '' : 's'}</Badge>
            </div>
          </Card>

          <Panel title={isEdit ? 'Save Changes' : 'Create User'} pad={14} style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
              <Btn variant="secondary" style={actionFace('green')}
                loading={saving} disabled={!canSave} onClick={save}>
                ✓ {isEdit ? 'Save Changes' : 'Create User'}
              </Btn>
              <Btn variant="secondary" style={actionFace('gray')}
                disabled={saving} onClick={() => navigate(backTo)}>
                ✕ Cancel
              </Btn>

              {/* Every outstanding problem at once, rather than one rejected
                  submit per rule. */}
              {problems.length > 0 && (
                <div style={{
                  fontSize: 11.5, color: 'var(--amber)', background: 'var(--amber-dim)',
                  border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8,
                  padding: '10px 12px', lineHeight: 1.6, flexShrink: 0,
                }}>
                  {problems.map((p) => <div key={p}>• {p}</div>)}
                </div>
              )}

              {saveError && (
                <div style={{
                  fontSize: 12, color: 'var(--red)', background: 'var(--red-dim)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
                  padding: '10px 12px', lineHeight: 1.55, flexShrink: 0,
                }}>
                  {saveError}
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* ── Right column ──────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          <Panel title="Personal Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <Input disabled={detailsLocked} label="First Name" required value={form.first_name} onChange={set('first_name')} />
              <Input disabled={detailsLocked} label="Last Name" required value={form.last_name} onChange={set('last_name')} />
              <div style={{ minWidth: 0 }}>
                <Input disabled={detailsLocked} label="Email Address" required type="email" value={form.email} onChange={set('email')}
                  placeholder="name@company.com" />
                {isEdit && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
                    This is their sign-in identity — changing it changes what they log in with.
                  </div>
                )}
              </div>
              <Input disabled={detailsLocked} label="Phone Number" value={form.phone} onChange={set('phone')} placeholder="+234…" />
            </div>
          </Panel>

          <Panel title="Account Access">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                {!isEdit && (
                  <Input label="Initial Password" required type="password" value={form.password}
                    onChange={set('password')} placeholder="8+ chars, an uppercase and a number" />
                )}
                <Select
                  label="Account Status" disabled={isEdit && !!me && !ua.deactivate} value={form.is_active ? 'yes' : 'no'}
                  onChange={(v) => setForm((f) => ({ ...f, is_active: v === 'yes' }))}
                  options={[{ value: 'yes', label: 'Active' }, { value: 'no', label: 'Inactive' }]}
                />
                <Select
                  label="Two-Step Sign-in" disabled={!!me && !ua.mfa} value={form.mfa_enabled ? 'yes' : 'no'}
                  onChange={(v) => setForm((f) => ({ ...f, mfa_enabled: v === 'yes' }))}
                  options={[{ value: 'no', label: 'Not required' }, { value: 'yes', label: 'Required (authenticator app)' }]}
                />
                {isEdit && (
                  <Select
                    label="Email Verified" disabled={!!me && !ua.edit} value={form.is_verified ? 'yes' : 'no'}
                    onChange={(v) => setForm((f) => ({ ...f, is_verified: v === 'yes' }))}
                    options={[{ value: 'yes', label: 'Verified' }, { value: 'no', label: 'Unverified' }]}
                  />
                )}
              </div>

              {/* Sits beside the initial password, which is what it governs.
                  Deliberately no expiry, unlike an admin password reset: a new
                  joiner may not sign in for days, and a clock would lock them
                  out of an account they had never used. */}
              {!isEdit && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  padding: '12px 14px', borderRadius: 8,
                }}>
                  <input
                    type="checkbox" style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                    checked={!!form.force_password_change}
                    onChange={(e) => setForm((f) => ({ ...f, force_password_change: e.target.checked }))}
                  />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Force a password change at first sign-in
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>
                      {form.force_password_change
                        ? 'You know the password above, so they must replace it before using the system.'
                        : 'They can keep using the password you set here indefinitely.'}
                    </span>
                  </span>
                </label>
              )}

              {!isEdit && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  padding: '12px 14px', borderRadius: 8,
                }}>
                  <input
                    type="checkbox" style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                    checked={!!form.skip_verification}
                    onChange={(e) => setForm((f) => ({ ...f, skip_verification: e.target.checked }))}
                  />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Create as verified — skip email verification
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>
                      {form.skip_verification
                        ? 'They will be emailed their account details and can sign in straight away.'
                        : 'They will be emailed a verification link and must activate the account before signing in.'}
                    </span>
                  </span>
                </label>
              )}

              {isEdit && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  To change this person's password, use <strong>Reset Password</strong> on their
                  profile — it issues a password you hand to them directly.
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Staff Record">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              <Input disabled={detailsLocked} label="Employee ID" value={form.employee_id} onChange={set('employee_id')} />
              <Input disabled={detailsLocked} label="Job Title" value={form.job_title} onChange={set('job_title')} />
              <Input disabled={detailsLocked} label="Grade" value={form.grade} onChange={set('grade')} />
              <Input disabled={detailsLocked} label="Branch" value={form.branch} onChange={set('branch')} />
            </div>
          </Panel>

          <Panel title={`Roles (${form.role_ids.length} selected)`}>
            {/* the Super Administrator role is only shown to super admins */}
            {roles.length ? checkboxGrid(roles.filter(r => ua.isSuper || r.role_name !== 'super_admin'), 'role_ids', (r) => (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{r.display_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>{r.role_name}</div>
                {r.description && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.45 }}>{r.description}</div>
                )}
              </>
            ), (r) => rolesLocked || (r.role_name === 'super_admin' && !ua.isSuper)) : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No roles defined yet.</div>}
            {isSelfEdit && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>You cannot change your own roles.</div>}
            {!isEdit && form.role_ids.length === 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.55 }}>
                Leave every box unticked and the account is created with the basic
                <strong> User</strong> role.
              </div>
            )}
          </Panel>

          <Panel title={`Permissions (${ov.grants.size} granted · ${ov.revokes.size} revoked for this user)`}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.55 }}>
              The ticked roles fill this in. Untick a permission to take it away from this user only, or tick one no role gives to
              grant it to them. Changing the roles clears the revocations.
            </div>
            <UserPermissionMatrix taxonomy={MODULE_TAXONOMY} rolePerms={rolePerms} grants={ov.grants} revokes={ov.revokes}
                                  onChange={setOv} locked={rolesLocked} isSuper={isSuperSelected} />
            {isSelfEdit && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>You cannot change your own permissions.</div>}
          </Panel>

          <Panel title={`Departments (${form.department_ids.length} selected)`}>
            {departments.length ? checkboxGrid(departments, 'department_ids', (d) => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{d.name}</div>
                {d.description && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.45 }}>{d.description}</div>
                )}
              </>
            ), () => detailsLocked) : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No departments defined yet.</div>}
          </Panel>
        </div>
      </div>

      {outcome && (
        <Modal title={outcome.title} onClose={() => navigate(outcome.goTo)} width={460}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '6px 0' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800,
                background: 'color-mix(in srgb, var(--green) 14%, transparent)',
                color: 'var(--green)',
                border: '1px solid color-mix(in srgb, var(--green) 35%, transparent)',
              }}>✓</span>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {outcome.message}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => navigate('/admin?tab=users')}>Back to Users</Btn>
              <Btn onClick={() => navigate(outcome.goTo)}>View Profile</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
